import React, { useRef, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { Badge } from 'primereact/badge';
import axios from 'axios';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import esLocale from '@fullcalendar/core/locales/es';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import LoadingOverlay from '../components/LoadingOverlay';
import { useTerminologia } from '../hooks/useTerminologia';

const lbl = { display: 'block', marginBottom: '0.3rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };
const badgePermanente = { background: '#eef4ff', color: '#3538cd', border: '1px solid #c7d7fe', fontWeight: 600, fontSize: '0.7rem', padding: '0.1rem 0.5rem', borderRadius: 99 };

const fmtHora = (v) => v ? new Date(v).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }) : '';
const fmtFechaHora = (v) => v ? new Date(v).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : '';
const fmtFechaCorta = (v) => v ? new Date(v).toLocaleDateString('es-AR') : '';

const vencidoBadge = (fecha) => {
    if (!fecha) return null;
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    if (new Date(fecha) >= hoy) return null;
    return (
        <span style={{
            background: '#fef3f2', color: '#f04438', border: '1px solid #fecdca',
            fontWeight: 600, fontSize: '0.7rem', padding: '0.1rem 0.5rem', borderRadius: 99, marginLeft: 6,
        }}>
            Vencido
        </span>
    );
};

const vencimientoCell = (fecha) => fecha
    ? <>{fmtFechaCorta(fecha)}{vencidoBadge(fecha)}</>
    : <span style={{ color: '#aaa' }}>—</span>;

// ════════════════════════════════════════════════════════════════════════════
// Vista Portero/Encargado (rolId 3): Control de Acceso
// ════════════════════════════════════════════════════════════════════════════

const FORM_INGRESO_EMPTY = {
    visitaId: null,
    unidadFuncionalId: null,
    nombreVisitante: '',
    tipoVisitaId: null,
    patente: '',
    empresa: '',
    observaciones: '',
    tipoVehiculoId: null,
    dniConductor: '',
    aseguradora: '',
    nroPoliza: '',
    fechaPoliza: null,
    fechaVencimientoSeguro: null,
    registro: '',
    fechaVencimientoRegistro: null,
};

const ControlAccesoPortero = () => {
    const toast = useRef(null);
    const HOST_API = process.env.REACT_APP_URL;
    const { barrio, seleccionarBarrio } = useContext(BarrioContext);
    const t = useTerminologia();
    const administracionId = parseInt(localStorage.getItem('administracionId') || '1');
    const usuarioId = JSON.parse(localStorage.getItem('user') || '{}').id
        || JSON.parse(localStorage.getItem('user') || '{}').usuarioId
        || 0;

    const [visitasDelDia, setVisitasDelDia] = useState([]);
    const [dentro, setDentro] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fechaVisitas, setFechaVisitas] = useState(new Date());
    const [filtroTexto, setFiltroTexto] = useState('');
    const [filtroTipoVisitaId, setFiltroTipoVisitaId] = useState(null);

    const [historial, setHistorial] = useState([]);
    const [loadingHistorial, setLoadingHistorial] = useState(false);
    const [histDesde, setHistDesde] = useState(new Date());
    const [histHasta, setHistHasta] = useState(new Date());

    const [ufs, setUfs] = useState([]);
    const [tipos, setTipos] = useState([]);
    const [tiposVehiculo, setTiposVehiculo] = useState([]);

    const [personas, setPersonas] = useState([]);
    const [fotosPersonas, setFotosPersonas] = useState({});
    const [personaSimulada, setPersonaSimulada] = useState(null);
    const [simulando, setSimulando] = useState(false);
    const [modalBarrera, setModalBarrera] = useState(false);
    const [detalleBarrera, setDetalleBarrera] = useState(null);

    const [modalIngreso, setModalIngreso] = useState(false);
    const [formIngreso, setFormIngreso] = useState({ ...FORM_INGRESO_EMPTY });
    const [submitting, setSubmitting] = useState(false);
    const [archivoSeguro, setArchivoSeguro] = useState(null);

    // ─── Auto-selección de barrio para portero ────────────────────────────────
    useEffect(() => {
        if (barrio?.id) return;
        const barriosPermitidos = JSON.parse(localStorage.getItem('barriosPermitidos') || '[]');
        if (barriosPermitidos.length === 0) return;
        axios.get(`${HOST_API}api/barrios/GetBarrios`, {
            params: { barrioId: barriosPermitidos[0], administracionId }
        }).then(({ data }) => {
            const lista = Array.isArray(data) ? data : [data];
            const b = lista[0];
            if (b) seleccionarBarrio(b.barrioId, b.nombreBarrio, b.cuit, b.direccion, b.razonSocial, b.numeroCuenta, b.cuentaBancaria, b.tipoCuentaBancaria, b.alias);
        }).catch(() => {});
    }, []);

    // ─── Carga UFs y tipos de visita (para el alta manual) ────────────────────
    useEffect(() => {
        if (!barrio?.id) return;
        axios.get(`${HOST_API}api/unidadesFuncionales/getUnidadesFuncionales?unidadFuncionalId=0&administracionId=${administracionId}&barrioId=${barrio.id}`)
            .then(({ data }) => setUfs(data))
            .catch(() => {});
        axios.get(`${HOST_API}api/tipovisita/GetTipoVisitas`)
            .then(({ data }) => setTipos(data))
            .catch(() => {});
        axios.get(`${HOST_API}api/tipovehiculo/GetTipoVehiculos`)
            .then(({ data }) => setTiposVehiculo(data))
            .catch(() => {});
        axios.get(`${HOST_API}api/personas/GetPersonas?administracionId=${administracionId}&personaId=0&barrioId=${barrio.id}`)
            .then(({ data }) => setPersonas(data || []))
            .catch(() => {});
        axios.get(`${HOST_API}api/personas/GetFotosPersonas?administracionId=${administracionId}&personaId=0&barrioId=${barrio.id}`)
            .then(({ data }) => {
                const mapa = {};
                (data || []).forEach((f) => { mapa[f.personaId] = f.fotoUrl; });
                setFotosPersonas(mapa);
            })
            .catch(() => {});
    }, [barrio?.id]);

    // ─── Fetch ──────────────────────────────────────────────────────────────────
    const fetchTodo = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            const fecha = (fechaVisitas || new Date()).toISOString().split('T')[0];
            const [resDia, resDentro] = await Promise.all([
                axios.get(`${HOST_API}api/controlAcceso/GetVisitasDelDia`, { params: { administracionId, barrioId: barrio.id, fecha } }),
                axios.get(`${HOST_API}api/controlAcceso/GetDentroDelBarrio`, { params: { administracionId, barrioId: barrio.id } }),
            ]);
            setVisitasDelDia(resDia.data || []);
            setDentro(resDentro.data || []);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar el control de acceso', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [HOST_API, barrio?.id, fechaVisitas]);

    useEffect(() => { fetchTodo(); }, [fetchTodo]);

    // ─── Unificación: visitas del día + quienes siguen dentro (aunque hayan
    // ingresado otro día) ──────────────────────────────────────────────────
    const filasIngresosEgresos = useMemo(() => {
        const controlAccesoIdsDelDia = new Set(
            visitasDelDia.filter(v => v.controlAccesoId).map(v => v.controlAccesoId)
        );
        const dentroExtra = dentro.filter(d => !controlAccesoIdsDelDia.has(d.controlAccesoId));
        const unificadas = [...visitasDelDia, ...dentroExtra];

        const texto = filtroTexto.trim().toLowerCase();
        return unificadas.filter(row => {
            if (filtroTipoVisitaId && row.tipoVisitaId !== filtroTipoVisitaId) return false;
            if (!texto) return true;
            return (row.patente || '').toLowerCase().includes(texto)
                || (row.nombreVisitante || '').toLowerCase().includes(texto)
                || (row.dniConductor || '').toLowerCase().includes(texto);
        });
    }, [visitasDelDia, dentro, filtroTexto, filtroTipoVisitaId]);

    const fetchHistorial = useCallback(async () => {
        if (!barrio?.id) return;
        setLoadingHistorial(true);
        try {
            const params = {
                administracionId,
                barrioId: barrio.id,
                fechaDesde: (histDesde || new Date()).toISOString().split('T')[0],
                fechaHasta: (histHasta || new Date()).toISOString().split('T')[0],
            };
            const { data } = await axios.get(`${HOST_API}api/controlAcceso/GetHistorial`, { params });
            setHistorial(data || []);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar el historial de accesos', life: 3000 });
        } finally {
            setLoadingHistorial(false);
        }
    }, [HOST_API, barrio?.id, histDesde, histHasta]);

    useEffect(() => { fetchHistorial(); }, [fetchHistorial]);

    // ─── Autocompletar por DNI (visitante frecuente) ──────────────────────────────
    const buscarVisitanteFrecuente = async (dni) => {
        const value = (dni || '').trim();
        if (!value || !barrio?.id) return;
        try {
            const { data } = await axios.get(`${HOST_API}api/visitanteFrecuente/BuscarPorDni`, {
                params: { administracionId, barrioId: barrio.id, dniConductor: value },
            });
            if (!data) return;
            setFormIngreso(prev => ({
                ...prev,
                nombreVisitante:          prev.nombreVisitante || data.nombreVisitante || prev.nombreVisitante,
                patente:                  prev.patente || data.patente || prev.patente,
                tipoVehiculoId:           prev.tipoVehiculoId ?? data.tipoVehiculoId ?? prev.tipoVehiculoId,
                aseguradora:              prev.aseguradora || data.aseguradora || prev.aseguradora,
                nroPoliza:                prev.nroPoliza || data.nroPoliza || prev.nroPoliza,
                fechaPoliza:              prev.fechaPoliza || (data.fechaPoliza ? new Date(data.fechaPoliza) : prev.fechaPoliza),
                fechaVencimientoSeguro:   prev.fechaVencimientoSeguro || (data.fechaVencimientoSeguro ? new Date(data.fechaVencimientoSeguro) : prev.fechaVencimientoSeguro),
                registro:                 prev.registro || data.registro || prev.registro,
                fechaVencimientoRegistro: prev.fechaVencimientoRegistro || (data.fechaVencimientoRegistro ? new Date(data.fechaVencimientoRegistro) : prev.fechaVencimientoRegistro),
            }));
            toast.current.show({ severity: 'info', summary: 'Datos encontrados', detail: 'Se completaron los datos guardados de un ingreso anterior con este DNI', life: 3500 });
        } catch {
            // búsqueda opcional: si falla no bloquea la carga manual
        }
    };

    // ─── Acciones ───────────────────────────────────────────────────────────────
    const abrirIngresoDesdeVisita = (visita) => {
        setFormIngreso({
            visitaId: visita.visitaId,
            unidadFuncionalId: visita.unidadFuncionalId,
            nombreVisitante: visita.nombreVisitante || '',
            tipoVisitaId: visita.tipoVisitaId,
            patente: visita.patente || '',
            empresa: visita.empresa || '',
            observaciones: visita.observaciones || '',
            tipoVehiculoId: visita.tipoVehiculoId,
            dniConductor: visita.dniConductor || '',
            aseguradora: visita.aseguradora || '',
            nroPoliza: visita.nroPoliza || '',
            fechaPoliza: visita.fechaPoliza ? new Date(visita.fechaPoliza) : null,
            fechaVencimientoSeguro: visita.fechaVencimientoSeguro ? new Date(visita.fechaVencimientoSeguro) : null,
            registro: visita.registro || '',
            fechaVencimientoRegistro: visita.fechaVencimientoRegistro ? new Date(visita.fechaVencimientoRegistro) : null,
        });
        setArchivoSeguro(null);
        setModalIngreso(true);
        if (visita.dniConductor) buscarVisitanteFrecuente(visita.dniConductor);
    };

    const registrarEgreso = (controlAccesoId, nombreVisitante) => {
        confirmDialog({
            message: `¿Registrar la salida de ${nombreVisitante}?`,
            header: 'Confirmar egreso',
            icon: 'pi pi-sign-out',
            acceptLabel: 'Registrar salida',
            rejectLabel: 'Cancelar',
            accept: async () => {
                try {
                    await axios.post(`${HOST_API}api/controlAcceso/RegistrarEgreso`, null, {
                        params: { controlAccesoId, porteroEgresoId: usuarioId },
                    });
                    toast.current.show({ severity: 'success', summary: 'Egreso registrado', detail: nombreVisitante, life: 3000 });
                    fetchTodo();
                    fetchHistorial();
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo registrar el egreso', life: 3000 });
                }
            },
        });
    };

    // ─── Simulador de barrera + iris (demo, ver plan de hardware) ─────────────
    const simularEventoBarrera = async (evento) => {
        const persona = personas.find(p => p.personaId === personaSimulada);
        if (!persona) { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccioná una persona para simular', life: 3000 }); return; }
        if (!persona.dni) { toast.current.show({ severity: 'warn', summary: 'Sin DNI', detail: 'Esa persona no tiene DNI cargado', life: 3000 }); return; }

        setSimulando(true);
        try {
            const { data } = await axios.post(`${HOST_API}api/controlAcceso/RegistrarEventoBarrera`, {
                administracionId, barrioId: barrio.id, dni: persona.dni, evento, fechaEvento: new Date(),
            });

            setDetalleBarrera({
                evento,
                nombreVisitante: data?.nombreVisitante || persona.nombreCompleto,
                codigoUF: data?.codigoUF,
                patente: data?.patente,
                fotoUrl: fotosPersonas[persona.personaId],
            });
            setModalBarrera(true);
            fetchTodo();
            fetchHistorial();
        } catch (err) {
            toast.current.show({
                severity: 'error', summary: 'Error',
                detail: err?.response?.data?.errorDisplay || 'No se pudo simular el evento',
                life: 4000,
            });
        } finally {
            setSimulando(false);
        }
    };

    const guardarIngresoManual = async () => {
        if (!formIngreso.unidadFuncionalId) { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccioná la unidad funcional', life: 3000 }); return; }
        if (!formIngreso.nombreVisitante.trim()) { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingresá el nombre del visitante', life: 3000 }); return; }

        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append('model', JSON.stringify({
                administracionId,
                barrioId: barrio.id,
                visitaId: formIngreso.visitaId || null,
                unidadFuncionalId: formIngreso.unidadFuncionalId,
                nombreVisitante: formIngreso.nombreVisitante,
                tipoVisitaId: formIngreso.tipoVisitaId,
                patente: formIngreso.patente || null,
                empresa: formIngreso.empresa || null,
                observaciones: formIngreso.observaciones || null,
                porteroIngresoId: usuarioId,
                tipoVehiculoId: formIngreso.tipoVehiculoId,
                dniConductor: formIngreso.dniConductor || null,
                aseguradora: formIngreso.aseguradora || null,
                nroPoliza: formIngreso.nroPoliza || null,
                fechaPoliza: formIngreso.fechaPoliza,
                fechaVencimientoSeguro: formIngreso.fechaVencimientoSeguro,
                registro: formIngreso.registro || null,
                fechaVencimientoRegistro: formIngreso.fechaVencimientoRegistro,
            }));
            if (archivoSeguro) fd.append('files', archivoSeguro);

            await axios.post(`${HOST_API}api/controlAcceso/RegistrarIngreso`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            toast.current.show({ severity: 'success', summary: 'Ingreso registrado', detail: formIngreso.nombreVisitante, life: 3000 });
            setModalIngreso(false);
            setFormIngreso({ ...FORM_INGRESO_EMPTY });
            setArchivoSeguro(null);
            fetchTodo();
            fetchHistorial();
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo registrar el ingreso', life: 3000 });
        } finally {
            setSubmitting(false);
        }
    };

    // ─── Templates ──────────────────────────────────────────────────────────────
    const ESTADO_ACCESO_COLORS = {
        'No ingresó': { bg: '#fffaeb', color: '#b54708', border: '#fedf89' },
        'Dentro':     { bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5' },
        'Egresó':     { bg: '#f2f4f7', color: '#667085', border: '#d0d5dd' },
    };

    const estadoTemplate = (row) => {
        const label = row.fechaEgreso ? 'Egresó' : row.fechaIngreso ? 'Dentro' : 'No ingresó';
        const s = ESTADO_ACCESO_COLORS[label];
        return (
            <span style={{
                background: s.bg, color: s.color, border: `1px solid ${s.border}`,
                fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99,
            }}>
                {label}
            </span>
        );
    };

    const origenTemplate = (row) => {
        if (row.origen !== 'Barrera') return <span style={{ color: '#aaa' }}>—</span>;
        return (
            <span style={{
                background: '#f4ebff', color: '#6941c6', border: '1px solid #d6bbfb',
                fontWeight: 600, fontSize: '0.7rem', padding: '0.15rem 0.6rem', borderRadius: 99,
            }}>
                👁 Barrera
            </span>
        );
    };

    const accionesDelDiaTemplate = (row) => {
        if (row.fechaEgreso) return null; // ya ingresó y egresó, no hay más acciones
        if (row.fechaIngreso) {
            return <Button label="Registrar salida" icon="pi pi-sign-out" size="small" severity="secondary"
                onClick={() => registrarEgreso(row.controlAccesoId, row.nombreVisitante)} />;
        }
        return <Button label="Registrar ingreso" icon="pi pi-sign-in" size="small"
            onClick={() => abrirIngresoDesdeVisita(row)} />;
    };

    // ─── Render ─────────────────────────────────────────────────────────────────
    return (
        <>
            <h4>Control de Acceso</h4>
            <br />
            <Toast ref={toast} />
            <ConfirmDialog />

<LoadingOverlay visible={loading} />

            <div className="flex align-items-center gap-2 mb-4">
                <Button label="Actualizar" icon="pi pi-refresh" className="p-button-sm" severity="secondary" onClick={fetchTodo} />
                <Button label="Ingreso sin aviso" icon="pi pi-plus" className="p-button-sm"
                    onClick={() => { setFormIngreso({ ...FORM_INGRESO_EMPTY }); setArchivoSeguro(null); setModalIngreso(true); }} />
            </div>

            {/* ─── Simulador de barrera + iris (demo) ─────────────────────────
                No hay hardware instalado todavía: este panel llama al mismo
                endpoint que va a usar la barrera real (RegistrarEventoBarrera),
                para poder mostrar el flujo antes de tener el sensor físico. */}
            <div style={{
                border: '1px dashed #7f56d9', borderRadius: 10, padding: '0.85rem 1rem', marginBottom: '1.5rem',
                background: '#f9f5ff', display: 'flex', alignItems: 'flex-end', gap: '0.75rem', flexWrap: 'wrap',
            }}>
                <div style={{ marginRight: 'auto' }}>
                    <div style={{ fontWeight: 700, color: '#6941c6', fontSize: '0.85rem' }}>👁 Simulador Barrera + Iris (demo)</div>
                    <div style={{ fontSize: '0.75rem', color: '#7f56d9' }}>Simula el evento que va a mandar el hardware cuando esté instalado</div>
                </div>
                <div>
                    <label style={lbl}>Persona</label>
                    <Dropdown value={personaSimulada} options={personas} optionLabel="nombreCompleto" optionValue="personaId"
                        onChange={(e) => setPersonaSimulada(e.value)}
                        placeholder="Seleccionar" filter showClear className="p-inputtext-sm" style={{ width: 240 }}
                        itemTemplate={(p) => `${p.nombreCompleto} — DNI ${p.dni || '—'}`} />
                </div>
                <Button label="Simular ingreso" icon="pi pi-sign-in" className="p-button-sm" severity="help"
                    onClick={() => simularEventoBarrera('Ingreso')} loading={simulando} />
                <Button label="Simular egreso" icon="pi pi-sign-out" className="p-button-sm" severity="help" outlined
                    onClick={() => simularEventoBarrera('Egreso')} loading={simulando} />
            </div>

            {/* ─── Ingresos / Egresos ──────────────────────────────────────── */}
            <div className="flex align-items-end justify-content-between flex-wrap gap-2 mb-2">
                <h5 style={{ margin: 0 }}>Ingresos / Egresos ({dentro.length} dentro ahora)</h5>
                <div className="flex align-items-end flex-wrap gap-3">
                    <div>
                        <label style={lbl}>Buscar</label>
                        <span className="p-input-icon-left">
                            <i className="pi pi-search" style={{ left: '0.75rem' }} />
                            <InputText value={filtroTexto} onChange={(e) => setFiltroTexto(e.target.value)}
                                placeholder="Patente, nombre o DNI…" className="p-inputtext-sm"
                                style={{ paddingLeft: '2rem', width: 200 }} />
                        </span>
                    </div>
                    <div>
                        <label style={lbl}>Tipo</label>
                        <Dropdown value={filtroTipoVisitaId} options={tipos} optionLabel="descripcion" optionValue="tipoVisitaId"
                            onChange={(e) => setFiltroTipoVisitaId(e.value)}
                            placeholder="Todos" showClear className="p-inputtext-sm" style={{ width: 160 }} />
                    </div>
                    <div>
                        <label style={lbl}>Fecha</label>
                        <Calendar value={fechaVisitas} onChange={(e) => setFechaVisitas(e.value)}
                            dateFormat="dd/mm/yy" showIcon showButtonBar
                            className="p-inputtext-sm" style={{ width: 170 }} />
                    </div>
                </div>
            </div>
            <div className="grid mb-4">
                <div className="col-12">
                    <DataTable value={filasIngresosEgresos} showGridlines responsiveLayout="scroll"
                        emptyMessage="No hay visitas ni ingresos para esta fecha"
                        className="p-datatable-gridlines bg-white" paginator rows={10}
                        sortField="fechaVisita" sortOrder={1}>
                        <Column field="codigoUF" header={t.UF} style={{ width: '7rem' }} />
                        <Column field="fechaVisita" header="Hora prevista" body={(r) => r.esPermanente
                            ? <span title={r.fechaFinalizacion ? `Hasta ${new Date(r.fechaFinalizacion).toLocaleDateString('es-AR')}` : 'Sin finalización'} style={badgePermanente}>Permanente</span>
                            : (r.fechaVisita ? fmtHora(r.fechaVisita) : '—')} style={{ width: '9rem' }} />
                        <Column field="nombreVisitante" header="Visitante" />
                        <Column field="tipoVisita" header="Tipo" style={{ width: '9rem' }} />
                        <Column field="patente" header="Patente" style={{ width: '8rem' }} />
                        <Column field="empresa" header="Empresa" />
                        <Column field="fechaIngreso" header="Ingresó" body={(r) => r.fechaIngreso ? fmtFechaHora(r.fechaIngreso) : '—'} style={{ width: '11rem' }} />
                        <Column field="fechaEgreso" header="Egresó" body={(r) => r.fechaEgreso ? fmtFechaHora(r.fechaEgreso) : '—'} style={{ width: '11rem' }} />
                        <Column header="Venc. Seguro" body={(r) => vencimientoCell(r.fechaVencimientoSeguro)} style={{ width: '9rem' }} />
                        <Column header="Venc. Registro" body={(r) => vencimientoCell(r.fechaVencimientoRegistro)} style={{ width: '9rem' }} />
                        <Column header="Estado" body={estadoTemplate} style={{ width: '8rem' }} />
                        <Column header="Vía" body={origenTemplate} style={{ width: '8rem' }} />
                        <Column header="Acciones" body={accionesDelDiaTemplate} style={{ width: '11rem', textAlign: 'center' }} />
                    </DataTable>
                </div>
            </div>

            {/* ─── Historial de ingresos/egresos (rango de fechas) ─────────── */}
            <div className="flex align-items-end flex-wrap gap-3 mb-2">
                <h5 style={{ margin: 0, marginRight: '0.5rem' }}>Historial de accesos</h5>
                <div>
                    <label style={lbl}>Desde</label>
                    <Calendar value={histDesde} onChange={(e) => setHistDesde(e.value)}
                        dateFormat="dd/mm/yy" showIcon showButtonBar
                        className="p-inputtext-sm" style={{ width: 170 }} />
                </div>
                <div>
                    <label style={lbl}>Hasta</label>
                    <Calendar value={histHasta} onChange={(e) => setHistHasta(e.value)}
                        dateFormat="dd/mm/yy" showIcon showButtonBar
                        className="p-inputtext-sm" style={{ width: 170 }} />
                </div>
                <Button label="Buscar" icon="pi pi-search" className="p-button-sm" onClick={fetchHistorial} />
            </div>
            <div className="grid">
                <div className="col-12">
                    <DataTable value={historial} showGridlines responsiveLayout="scroll"
                        loading={loadingHistorial}
                        emptyMessage="No hay accesos registrados en el período seleccionado"
                        className="p-datatable-gridlines bg-white" paginator rows={10}>
                        <Column field="codigoUF" header={t.UF} style={{ width: '7rem' }} />
                        <Column field="nombreVisitante" header="Visitante" />
                        <Column field="tipoVisita" header="Tipo" style={{ width: '9rem' }} />
                        <Column field="patente" header="Patente" style={{ width: '8rem' }} />
                        <Column field="fechaIngreso" header="Ingreso" body={(r) => fmtFechaHora(r.fechaIngreso)} style={{ width: '11rem' }} />
                        <Column field="porteroIngreso" header="Registró ingreso" style={{ width: '10rem' }} />
                        <Column field="fechaEgreso" header="Egreso" body={(r) => r.fechaEgreso ? fmtFechaHora(r.fechaEgreso) : '—'} style={{ width: '11rem' }} />
                        <Column field="porteroEgreso" header="Registró egreso" body={(r) => r.porteroEgreso || '—'} style={{ width: '10rem' }} />
                        <Column header="Venc. Seguro" body={(r) => vencimientoCell(r.fechaVencimientoSeguro)} style={{ width: '9rem' }} />
                        <Column header="Venc. Registro" body={(r) => vencimientoCell(r.fechaVencimientoRegistro)} style={{ width: '9rem' }} />
                        <Column header="Vía" body={origenTemplate} style={{ width: '8rem' }} />
                    </DataTable>
                </div>
            </div>

            {/* ─── Modal: ingreso sin aviso previo ─────────────────────────── */}
            <Dialog header={formIngreso.visitaId ? 'Registrar ingreso' : 'Registrar ingreso sin aviso'} visible={modalIngreso} style={{ width: '560px' }} modal
                onHide={() => setModalIngreso(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancelar" className="p-button-text p-button-sm" onClick={() => setModalIngreso(false)} disabled={submitting} />
                        <Button label="Registrar ingreso" icon="pi pi-sign-in" className="p-button-sm" onClick={guardarIngresoManual} loading={submitting} />
                    </div>
                }>
                <div className="grid p-fluid mt-1">
                    <div className="col-12">
                        <label style={lbl}>{t.UnidadFuncional} *</label>
                        <Dropdown value={formIngreso.unidadFuncionalId} options={ufs} optionLabel="unidadFuncional" optionValue="unidadFuncionalId"
                            onChange={(e) => setFormIngreso(f => ({ ...f, unidadFuncionalId: e.value }))}
                            placeholder={`Seleccionar ${t.UF}`} filter className="p-inputtext-sm" />
                    </div>
                    <div className="col-12">
                        <label style={lbl}>Nombre del visitante *</label>
                        <InputText value={formIngreso.nombreVisitante}
                            onChange={(e) => setFormIngreso(f => ({ ...f, nombreVisitante: e.target.value }))}
                            className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Tipo de visita</label>
                        <Dropdown value={formIngreso.tipoVisitaId} options={tipos} optionLabel="descripcion" optionValue="tipoVisitaId"
                            onChange={(e) => setFormIngreso(f => ({ ...f, tipoVisitaId: e.value }))}
                            placeholder="Seleccionar" className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Patente</label>
                        <InputText value={formIngreso.patente}
                            onChange={(e) => setFormIngreso(f => ({ ...f, patente: e.target.value }))}
                            className="p-inputtext-sm" />
                    </div>
                    <div className="col-12">
                        <label style={lbl}>Empresa</label>
                        <InputText value={formIngreso.empresa}
                            onChange={(e) => setFormIngreso(f => ({ ...f, empresa: e.target.value }))}
                            placeholder="Si es un proveedor/servicio" className="p-inputtext-sm" />
                    </div>
                    <div className="col-12">
                        <label style={lbl}>Observaciones</label>
                        <InputText value={formIngreso.observaciones}
                            onChange={(e) => setFormIngreso(f => ({ ...f, observaciones: e.target.value }))}
                            className="p-inputtext-sm" />
                    </div>

                    <div className="col-12">
                        <label style={{ ...lbl, fontWeight: 700, marginTop: '0.25rem' }}>Datos del vehículo (opcional)</label>
                    </div>

                    <div className="col-6">
                        <label style={lbl}>Tipo de vehículo</label>
                        <Dropdown value={formIngreso.tipoVehiculoId} options={tiposVehiculo} optionLabel="descripcion" optionValue="tipoVehiculoId"
                            onChange={(e) => setFormIngreso(f => ({ ...f, tipoVehiculoId: e.value }))}
                            placeholder="Seleccionar" className="p-inputtext-sm" showClear />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>DNI del conductor</label>
                        <InputText value={formIngreso.dniConductor}
                            onChange={(e) => setFormIngreso(f => ({ ...f, dniConductor: e.target.value }))}
                            onBlur={(e) => buscarVisitanteFrecuente(e.target.value)}
                            className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Registro (licencia)</label>
                        <InputText value={formIngreso.registro}
                            onChange={(e) => setFormIngreso(f => ({ ...f, registro: e.target.value }))}
                            className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Vencimiento del registro {vencidoBadge(formIngreso.fechaVencimientoRegistro)}</label>
                        <Calendar value={formIngreso.fechaVencimientoRegistro}
                            onChange={(e) => setFormIngreso(f => ({ ...f, fechaVencimientoRegistro: e.value }))}
                            dateFormat="dd/mm/yy" showIcon className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Aseguradora</label>
                        <InputText value={formIngreso.aseguradora}
                            onChange={(e) => setFormIngreso(f => ({ ...f, aseguradora: e.target.value }))}
                            className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>N° de póliza</label>
                        <InputText value={formIngreso.nroPoliza}
                            onChange={(e) => setFormIngreso(f => ({ ...f, nroPoliza: e.target.value }))}
                            className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Fecha de póliza</label>
                        <Calendar value={formIngreso.fechaPoliza}
                            onChange={(e) => setFormIngreso(f => ({ ...f, fechaPoliza: e.value }))}
                            dateFormat="dd/mm/yy" showIcon className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Vencimiento del seguro {vencidoBadge(formIngreso.fechaVencimientoSeguro)}</label>
                        <Calendar value={formIngreso.fechaVencimientoSeguro}
                            onChange={(e) => setFormIngreso(f => ({ ...f, fechaVencimientoSeguro: e.value }))}
                            dateFormat="dd/mm/yy" showIcon className="p-inputtext-sm" />
                    </div>
                    <div className="col-12">
                        <label style={lbl}>Adjuntar seguro (PDF, JPG o PNG)</label>
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => setArchivoSeguro(e.target.files[0] || null)} />
                        {archivoSeguro && (
                            <span style={{ fontSize: '0.8rem', color: '#667085', marginLeft: '0.5rem' }}>{archivoSeguro.name}</span>
                        )}
                    </div>
                </div>
            </Dialog>

            {/* ─── Modal: pantalla de la barrera al reconocer por iris ────────── */}
            <Dialog header={`${detalleBarrera?.evento === 'Egreso' ? 'Egreso' : 'Ingreso'} registrado (barrera)`} visible={modalBarrera} style={{ width: '680px' }} modal
                onHide={() => setModalBarrera(false)}
                footer={
                    <div className="flex justify-content-center">
                        <Button label="Aceptar" icon="pi pi-check" className="p-button-sm" onClick={() => setModalBarrera(false)} autoFocus />
                    </div>
                }>
                {detalleBarrera && (() => {
                    const esEgreso = detalleBarrera.evento === 'Egreso';
                    const color = esEgreso ? '#1570ef' : '#12b76a';
                    return (
                    <div style={{ textAlign: 'center', padding: '0.5rem 0 1rem' }}>
                        <div style={{ color, fontWeight: 700, fontSize: '1.1rem', marginBottom: '1rem' }}>
                            {esEgreso ? '🚪 SALIDA REGISTRADA' : '✅ ACCESO PERMITIDO'}
                        </div>
                        <div style={{ fontSize: '2.4rem', fontWeight: 700, color: '#101828', marginBottom: '1.25rem' }}>{detalleBarrera.nombreVisitante}</div>
                        {detalleBarrera.fotoUrl ? (
                            <img src={`${HOST_API}${detalleBarrera.fotoUrl}`.replace(/([^:])\/\/+/g, '$1/')} alt=""
                                style={{ display: 'block', width: '100%', maxWidth: 480, height: 300, borderRadius: 12, objectFit: 'cover', border: `4px solid ${color}`, margin: '0 auto 1.5rem' }} />
                        ) : (
                            <div style={{
                                width: '100%', maxWidth: 480, height: 300, borderRadius: 12, background: '#f2f4f7', color: '#98a2b3',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem',
                                border: `4px solid ${color}`, fontSize: '6rem',
                            }}>
                                <i className="pi pi-user" />
                            </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '4rem' }}>
                            <div>
                                <div style={{ fontSize: '1rem', color: '#667085', textTransform: 'uppercase' }}>{t.UF}</div>
                                <div style={{ fontWeight: 600, fontSize: '1.8rem' }}>{detalleBarrera.codigoUF || '—'}</div>
                            </div>
                            <div>
                                <div style={{ fontSize: '1rem', color: '#667085', textTransform: 'uppercase' }}>Patente</div>
                                <div style={{ fontWeight: 600, fontSize: '1.8rem' }}>{detalleBarrera.patente || '—'}</div>
                            </div>
                        </div>
                    </div>
                    );
                })()}
            </Dialog>
        </>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// Vista Administración (rolId 1): listado/calendario de visitas anunciadas
// ════════════════════════════════════════════════════════════════════════════

const VisitasAdminGrid = () => {
    const toast    = useRef(null);
    const HOST_API = process.env.REACT_APP_URL;
    const { barrio } = useContext(BarrioContext);
    const { administracionId } = useAuth();
    const t = useTerminologia();

    const [visitas,     setVisitas]     = useState([]);
    const [loading,     setLoading]     = useState(false);
    const [fechaDesde,  setFechaDesde]  = useState(null);
    const [fechaHasta,  setFechaHasta]  = useState(null);
    const [vistaCalend, setVistaCalend] = useState(false);

    // ─── Fetch ────────────────────────────────────────────────────────────────────

    const fetchVisitas = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({ administracionId, barrioId: barrio.id });
            if (fechaDesde) params.append('fechaDesde', fechaDesde.toISOString().split('T')[0]);
            if (fechaHasta) params.append('fechaHasta', fechaHasta.toISOString().split('T')[0]);
            const { data } = await axios.get(`${HOST_API}api/visitas/GetVisitasAdmin?${params}`);
            setVisitas(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar las visitas', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [HOST_API, barrio?.id, fechaDesde, fechaHasta, administracionId]);

    useEffect(() => { fetchVisitas(); }, [fetchVisitas]);

    // ─── Calendario ───────────────────────────────────────────────────────────────

    const eventos = useMemo(() => visitas.map(v => ({
        id:               String(v.visitaId),
        title:            `${v.nombreVisitante} · UF ${v.codigoUF || v.unidadFuncionalId}`,
        start:            v.fechaVisita,
        backgroundColor:  v.tipoVisita?.toLowerCase() === 'profesional' ? '#e67e22' : '#3498db',
        borderColor:      v.tipoVisita?.toLowerCase() === 'profesional' ? '#ca6f1e' : '#2980b9',
        extendedProps:    v,
    })), [visitas]);

    // ─── Render helpers ───────────────────────────────────────────────────────────

    const fmtFecha = (row) => {
        if (!row.fechaVisita) return '';
        if (!row.esPermanente) return new Date(row.fechaVisita).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
        const dia = (f) => new Date(f).toLocaleDateString('es-AR');
        return (
            <div className="flex flex-column gap-1">
                <span><span style={badgePermanente}>Permanente</span></span>
                <span style={{ fontSize: '0.78rem', color: '#667085' }}>
                    Desde {dia(row.fechaVisita)} · {row.fechaFinalizacion ? `hasta ${dia(row.fechaFinalizacion)}` : 'sin finalización'}
                </span>
            </div>
        );
    };

    const adjBadge = (row) => row.cantAdjuntos > 0
        ? <Badge value={row.cantAdjuntos} severity="info" />
        : <span style={{ color: '#aaa' }}>—</span>;

    // ─── Render ───────────────────────────────────────────────────────────────────

    return (
        <>
            <h4>Visitas — Administración</h4>
            <br />

            <Toast ref={toast} />

<LoadingOverlay visible={loading} />

            {/* ─── Filtros ─────────────────────────────────────────────────────── */}
            <div className="flex flex-wrap align-items-end gap-3 mb-4">
                <div>
                    <label style={lbl}>Desde</label>
                    <Calendar
                        value={fechaDesde}
                        onChange={(e) => setFechaDesde(e.value)}
                        dateFormat="dd/mm/yy"
                        showIcon
                        showButtonBar
                        className="p-inputtext-sm"
                        style={{ width: 170 }}
                        placeholder="Desde..."
                    />
                </div>
                <div>
                    <label style={lbl}>Hasta</label>
                    <Calendar
                        value={fechaHasta}
                        onChange={(e) => setFechaHasta(e.value)}
                        dateFormat="dd/mm/yy"
                        showIcon
                        showButtonBar
                        className="p-inputtext-sm"
                        style={{ width: 170 }}
                        placeholder="Hasta..."
                    />
                </div>
                <Button label="Buscar" icon="pi pi-search" onClick={fetchVisitas} className="p-button-sm" />
                <Button
                    label={vistaCalend ? 'Ver grilla' : 'Ver calendario'}
                    icon={vistaCalend ? 'pi pi-table' : 'pi pi-calendar'}
                    severity="secondary"
                    className="p-button-sm"
                    onClick={() => setVistaCalend(v => !v)}
                />
            </div>

            {/* ─── Vista grilla ────────────────────────────────────────────────── */}
            {!vistaCalend && (
                <div className="grid">
                    <div className="col-12">
                        <DataTable
                            value={visitas}
                            paginator
                            rows={15}
                            rowsPerPageOptions={[10, 15, 25, 50]}
                            showGridlines
                            responsiveLayout="scroll"
                            emptyMessage="No hay visitas para el período seleccionado"
                            className="p-datatable-gridlines bg-white"
                            sortField="fechaVisita"
                            sortOrder={-1}
                        >
                            <Column field="codigoUF"        header={t.UF}        sortable style={{ width: '8rem' }} />
                            <Column field="fechaVisita"     header="Fecha"     sortable body={fmtFecha} style={{ width: '11rem' }} />
                            <Column field="nombreVisitante" header="Visitante" sortable />
                            <Column field="tipoVisita"      header="Tipo"      sortable style={{ width: '9rem' }} />
                            <Column field="patente"         header="Patente"   style={{ width: '8rem' }} />
                            <Column field="empresa"         header="Empresa"   />
                            <Column field="observaciones"   header="Observaciones" />
                            <Column header="Venc. Seguro" body={(r) => vencimientoCell(r.fechaVencimientoSeguro)} style={{ width: '9rem' }} />
                            <Column header="Venc. Registro" body={(r) => vencimientoCell(r.fechaVencimientoRegistro)} style={{ width: '9rem' }} />
                            <Column header="Adj." style={{ width: '5rem', textAlign: 'center' }} body={adjBadge} />
                        </DataTable>
                    </div>
                </div>
            )}

            {/* ─── Vista calendario ────────────────────────────────────────────── */}
            {vistaCalend && (
                <div style={{ background: '#fff', borderRadius: 10, padding: '1rem', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.75rem', fontSize: '0.8rem', color: '#667085' }}>
                        <span><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: '#3498db', marginRight: 5 }} />Particular</span>
                        <span><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: '#e67e22', marginRight: 5 }} />Profesional</span>
                    </div>
                    <FullCalendar
                        plugins={[dayGridPlugin, timeGridPlugin]}
                        initialView="dayGridMonth"
                        locale={esLocale}
                        events={eventos}
                        headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,timeGridDay' }}
                        height="auto"
                        eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
                        eventClick={(info) => {
                            const v = info.event.extendedProps;
                            toast.current.show({
                                severity: 'info',
                                summary: v.nombreVisitante,
                                detail: `UF ${v.codigoUF} · ${v.tipoVisita}${v.empresa ? ' · ' + v.empresa : ''}${v.patente ? ' · ' + v.patente : ''}`,
                                life: 5000,
                            });
                        }}
                    />
                </div>
            )}
        </>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// Entry point: elige la vista según el rol logueado
// ════════════════════════════════════════════════════════════════════════════

const VisitasAdmin = () => {
    const rolId = parseInt(localStorage.getItem('rolId') || '0');
    return rolId === 3 ? <ControlAccesoPortero /> : <VisitasAdminGrid />;
};

export default VisitasAdmin;
